import React, { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import BedNeeded from './bed-needed';
import MyRequest from './my-request';
import useMyAgencyData from '../../../hooks/useMyAgencyData';
import useAgencies from "../../../hooks/useAgencies";
import { useNavigate } from 'react-router-dom';
import Loader from '../../../Components/Loader';
import { useFeature } from '../../../hooks/useFeature';
import { showToast } from "../../../Components/ToastNotification"
import ModalDailog from '../../../Components/Modal';
import { UpdateAgency } from '../../../type/Agency';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notificationsFormSchema, referralFormSchema, settingsFormSchema } from '../Settings/Schema/SettingsSchema';
import createAxiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from '../../../utils/api';
import InputBox from '../../../Components/Input';

export const titles: any = {
  welcome: "Welcome",
  notifications: "Notification preferences",
  contacts: "Contact preferences",
  thankyou: "Thank you!",
} as const;

type LocateShelterDashboardProps = {
  userData: any;
  triggerPopup: () => void;
};

const LocateShelterDashboard: React.FC<LocateShelterDashboardProps> = ({ userData, triggerPopup }) => {
  const axiosInstance = createAxiosInstance();
  const canAnswer = useFeature("bedfinder:canAnswerRequest");
  const isSender = useFeature("bedfinder:canSendRequest");
  const canReceiveRequest = useFeature("bedfinder:canReceiveRequest");
  const canSendRequest = useFeature("bedfinder:canSendRequest");
  const isObserver = !canAnswer && !isSender && canReceiveRequest;
  const isReferral = !canAnswer && isSender && !canReceiveRequest;
  const needContacts = canAnswer || !(isObserver || isReferral);
  const navigate = useNavigate();
  const myShelter = useMyAgencyData();
  const { agencies, agenciesById } = useAgencies();
  const [isBedNeededLoading, setIsBedNeededLoading] = useState(true);
  const [isMyRequestLoading, setMyRequestLoadingLoading] = useState(true);
  const [cachedResponseId, setCachedResponseId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState("");
  const [steps, setSteps] = useState<any>([]);
  const [isLoding, setIsLoding] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (myShelter) {
      setIsLoding(true);
      const step =
        myShelter.wizardStatus === "welcome"
          ? "notifications"
          : myShelter.wizardStatus === "notifications"
          ? needContacts
            ? "contacts"
            : "thankyou"
          : myShelter.wizardStatus === "contacts"
          ? "thankyou"
          : "welcome";
      setCurrentStep(step);
        
      reset({
        details: {
          notifications: [
            {
              email: myShelter?.details?.notifications?.[0]?.email || "",
              sms: myShelter?.details?.notifications?.[0]?.sms || "",
              voice: myShelter?.details?.notifications?.[0]?.voice || "",
            },
          ],
          referral: {
            phone: myShelter?.details?.referral?.phone || "",
            email: myShelter?.details?.referral?.email || "",
          },
        },
      });

      const showSettingsWizardPopup = myShelter.wizardStatus !== "thankyou";
      if (showSettingsWizardPopup) {
        setIsModalOpen(true);
      }
      setIsLoding(false);
    }
  }, [myShelter])

  useEffect(() => {
    const steps = !needContacts
    ? (Object.keys(titles) as (keyof typeof titles)[]).filter(
        breadcrumbItem => breadcrumbItem !== "contacts",
      )
    : (Object.keys(titles) as (keyof typeof titles)[]);
    setSteps(steps);
  }, [needContacts])

  const updateCachedResponseId = (id: string | null) => {
    setCachedResponseId(id);
  }

  const updateIsBedNeededLoading = () => {
    setIsBedNeededLoading(false);
  }

  const updateIsMyRequestLoading = () => {
    setMyRequestLoadingLoading(false);
  }

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleOnClick = () => {
    isSender
      ? navigate("/bedfinder/new-request")
      : showToast(
        "You don't have permission to create a new request. Please contact your administrator.", "error"
      );
  }

  const schema = currentStep === "notifications" ? notificationsFormSchema : currentStep === "contacts" ? referralFormSchema : settingsFormSchema;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<UpdateAgency>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      details: {
        notifications: [
          {
            email: myShelter?.details?.notifications?.[0]?.email || "",
            sms: myShelter?.details?.notifications?.[0]?.sms || "",
            voice: myShelter?.details?.notifications?.[0]?.voice || "",
          },
        ],
        referral: {
          phone: myShelter?.details?.referral?.phone || "",
          email: myShelter?.details?.referral?.email || "",
        },
      },
    },
    resolver: zodResolver(schema),
  });

  const handleNext: SubmitHandler<UpdateAgency> = async (data) => {
    const formData = getValues();
    try {
      if (myShelter) {
        setIsLoding(true);
        const payload = {
          "details.referral": formData.details?.referral,
          "details.notifications": formData.details?.notifications,
          "details.phone": formData.details?.notifications?.[0]?.phone,
          "details.sms": formData.details?.notifications?.[0]?.sms,
          "details.email": formData.details?.notifications?.[0]?.email,
          "details.voice": formData.details?.notifications?.[0]?.voice,
          wizardStatus: currentStep,
        };

        const saveSettings = await updateAgency(payload);

        if (saveSettings?.status === 200) {
          const currentIndex = steps.indexOf(currentStep);
          setCurrentStep(steps[currentIndex + 1]);
          setIsLoding(false);
          showToast("Settings were saved.", "success");
        }
      }
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  const submitWelcomeWiz = async () => {
    const formData = getValues();
    
    const payload = {
      "details.referral": formData?.details?.referral,
      "details.notifications": formData?.details?.notifications,
      "details.phone": formData?.details?.notifications?.[0]?.phone,
      "details.sms": formData?.details?.notifications?.[0]?.sms,
      "details.email": formData?.details?.notifications?.[0]?.email,
      "details.voice": formData?.details?.notifications?.[0]?.voice,
    };

    await updateAgency({ ...payload, wizardStatus: "thankyou" });

    closeModal();
  }

  const updateAgency = async (payload: any) => {
    if (myShelter?.agencyId) {
      return await axiosInstance.put(API_ENDPOINTS.BEDFINDER.UPDATEUSERSETTINGS.replace("{agencyId}", myShelter?.agencyId), payload);
    }
  }

  const handleBack = async () => {
    setIsLoding(true);
    const formData = getValues();
    const payload = {
      "details.referral": formData?.details?.referral,
      "details.notifications": formData?.details?.notifications,
      "details.phone": formData?.details?.notifications?.[0]?.phone,
      "details.sms": formData?.details?.notifications?.[0]?.sms,
      "details.email": formData?.details?.notifications?.[0]?.email,
      "details.voice": formData?.details?.notifications?.[0]?.voice,
    };
    if (currentStep === "welcome") {
      await updateAgency({ ...payload, wizardStatus: null });
    }
    if (currentStep === "notifications") {
      await updateAgency({ ...payload, wizardStatus: "welcome" });
    }
    if (currentStep === "contacts") {
      await updateAgency({ ...payload, wizardStatus: "notifications" });
    }
    if (currentStep === "thankyou") {
      await updateAgency({ ...payload, wizardStatus: "notifications" });
    }
    const currentIndex = steps.indexOf(currentStep);
    setCurrentStep(steps[currentIndex - 1]);
    setIsLoding(false);
  };

  useEffect(() => {
    if (userData && userData.hasSeenNewUiPopup !== true) {
      triggerPopup();
    }
  }, [userData]);

  return (
    <>
      {isLoding && <Loader />}
      <div className="container py-4 md:py-10 2xl:py-[70px]">
        <div className="mb-5 2xl:mb-10 flex justify-center">
          <button onClick={handleOnClick} className="btn btn-primary bg-[#DD5226] hover:bg-orange-400 rounded-full">
            <FiPlus size={20} /> Find a Bed
          </button>
        </div>
        {/* Tabs */}
        <div className="mb-6">
          {canReceiveRequest &&
            <>
              {(isBedNeededLoading && isMyRequestLoading) && <Loader />}
              <p
                className={`py-3 text-lg 2xl:text-xl font-semibold`}
              >
                Requests We Received
              </p>
              <div>
                <BedNeeded myShelter={myShelter} agenciesById={agenciesById} updateIsBedNeededLoading={updateIsBedNeededLoading} updateCachedResponseId={updateCachedResponseId} />
              </div>
            </>
          }
          {canSendRequest &&
            <>
              <p
                className={`mt-8 text-lg 2xl:text-xl font-semibold `}
              >
                Requests We Sent
              </p>
              <div>
                <MyRequest myShelter={myShelter} agencies={agencies} agenciesById={agenciesById} cachedResponseId={cachedResponseId} updateIsMyRequestLoading={updateIsMyRequestLoading} updateCachedResponseId={updateCachedResponseId} />
              </div>
            </>
          }
        </div>

        {/* Tab Content */}

      </div>
      <ModalDailog isOpen={isModalOpen} onClose={closeModal} handleSubmit={handleSubmit} closeRequest={handleNext} title={titles[currentStep]} isFooterAvailable={false} isXAvailable={false}>
        <div className="w-full">
          {/* Step Progress Bar */}
          <div className="flex justify-between mb-8 relative">
            {steps.map((step: any, index: any) => (
              <div key={index} className="flex-1 flex flex-col items-center relative">
                {/* Line Connector - Only show after first circle */}
                {index > 0 && (
                  <div className="absolute -left-1/2 top-4 w-full h-0.5 bg-gray-300 z-0" />
                )}

                {/* Circle */}
                <div
                  className={`w-8 h-8 z-10 relative rounded-full flex items-center justify-center font-bold ${step === currentStep ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}
                >
                  {index + 1}
                </div>

                {/* Label */}
                <div className="text-xs mt-2 text-center">{titles[step]}</div>
              </div>
            ))}
          </div>


          {/* Form Content */}
          <div className="mb-6 px-10">
            {currentStep === "welcome" && (
              <div className="card rounded-md shadow-none">
                <p className='text-gray-900 text-base font-semibold'>We're glad you're here!</p>
                <p className='mt-3 text-gray-700 text-sm'>One more step: please let us know how your agency wants to receive important alerts.</p>
              </div>
            )}

            {currentStep === "notifications" && (
              <>
                <p className='text-gray-900 text-base font-semibold'>BED REQUEST ALERTS:</p>
                <p className='mt-3 text-gray-700 text-sm'>How does your agency wants to receive notification?</p>
                {(isObserver || needContacts) && (
                  <div className="flex gap-3 justify-center items-start mt-3">
                    <label className="block font-medium w-1/2 text-end pt-2">
                      Call my agency with an automated voice message
                    </label>
                    <div className="w-1/2">
                      <InputBox
                        type="text"
                        placeholder="Enter a phone number"
                        inputClass="form-controls w-full"
                        {...register("details.notifications.0.voice")}
                      />
                      {errors.details?.notifications?.[0]?.voice?.message && (
                        <p className='text-error text-sm mt-1'>{errors.details?.notifications?.[0]?.voice?.message}</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 justify-center items-start mt-3">
                  <label className="block font-medium w-1/2 text-end pt-2">
                    Send my agency an email
                  </label>
                  <div className="w-1/2">
                    <InputBox
                      type="email"
                      placeholder="Enter an email address"
                      inputClass="form-control w-full"
                      {...register("details.notifications.0.email")}
                    />
                    {errors.details?.notifications?.[0]?.email?.message && (
                      <p className='text-error text-sm mt-1'>{errors.details?.notifications?.[0]?.email?.message}</p>
                    )}
                  </div>
                </div>
                {(isObserver || needContacts) && (
                  <div className="flex gap-3 justify-center items-start mt-3">
                    <label className="block font-medium w-1/2 text-end pt-2">
                      Send my agency a text (SMS) message
                    </label>
                    <div className="w-1/2">
                      <InputBox
                        type="text"
                        placeholder="Enter a mobile number"
                        inputClass="w-full"
                        {...register("details.notifications.0.sms")}
                      />
                      {errors.details?.notifications?.[0]?.sms?.message && (
                        <p className='text-error text-sm mt-1'>{errors.details?.notifications?.[0]?.sms?.message}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {currentStep === "contacts" &&  (
              <>
                <p className='text-gray-900 text-base font-semibold'>REFERRAL PREFERENCES:</p>
                <p className='mt-3 text-gray-700 text-sm'>Enter the agency contact information Safe Shelter Collaborative members should use to discuss a possible referral</p>
                <div className="flex gap-3 justify-center items-center mt-3">
                  <label className="block mb-2 font-medium w-1/2 text-end">Phone number for referrals:</label>
                  <InputBox
                    type="text"
                    placeholder="Enter a phone number"
                    formGroupClass="w-1/2"
                    value={watch("details.referral.phone")}
                    {...register("details.referral.phone")}
                  />
                </div>
                <div className="flex gap-3 justify-center items-center mt-3">
                  <label className="block mb-2 font-medium w-1/2 text-end">Email address for referrals:</label>
                  <InputBox
                    type="email"
                    placeholder="Enter an email address"
                    formGroupClass="w-1/2"
                    value={watch("details.referral.email")}
                    {...register("details.referral.email")}
                  />
                </div>
              </>
            )}
            {currentStep === "thankyou" && (
              <div className="card rounded-md shadow-none">
                <p className='text-gray-900 text-base font-semibold'>Thank You!</p>
                <p className='mt-3 text-gray-900 text-base font-medium'>You have entered the following Preferences:</p>
                {watch("details.notifications.0.voice") && (isObserver || needContacts) && (
                  <p className='mt-3 text-gray-700 text-sm'>Call my agency with an automated voice message: <b>{watch("details.notifications.0.voice")}</b></p>
                )}
                {watch("details.notifications.0.email") && (
                  <p className='mt-3 text-gray-700 text-sm'>Send my agency an email: <b>{watch("details.notifications.0.email")}</b></p>
                )}
                {watch("details.notifications.0.sms") && (isObserver || needContacts) && (
                  <p className='mt-3 text-gray-700 text-sm'>Send my agency a text (SMS) message: <b>{watch("details.notifications.0.sms")}</b></p>
                )}
                {watch("details.referral.phone") && needContacts && (
                  <p className='mt-3 text-gray-700 text-sm'>Phone number for referrals: <b>{watch("details.referral.phone")}</b></p>
                )}
                {watch("details.referral.email") && needContacts && (
                  <p className='mt-3 text-gray-700 text-sm'>Email address for referrals: <b>{watch("details.referral.email")}</b></p>
                )}
                <p className='mt-3 text-gray-900 text-base font-semibold'>You can review and change these preferences at any time by clicking the "Setting" tab.</p>

              </div>
            )}
            {errors.form && <p className='flex justify-end text-error mt-3'>{errors.form.message as string}</p>}
          </div>

          {/* Buttons */}
          <div className="flex justify-between mt-8 px-10">
            {currentStep !== "welcome" ? (
              <button
                type='button'
                onClick={handleBack}
                disabled={currentStep === "welcome"}
                className="px-4 py-2 bg-gray-300 text-black rounded disabled:opacity-50"
              >
                Back
              </button>
            ) : <div></div>}
            {currentStep !== "thankyou" && (
              <button
                type={currentStep === "welcome" ? 'button' : 'submit'}
                onClick={() => {
                  if (currentStep === 'welcome') {
                    const formData = getValues();
                    handleNext(formData);
                  }
                }}
                className={`px-4 py-2 bg-blue-600 text-white rounded`}
              >
                Save and Continue
              </button>
            )}
            {currentStep === "thankyou" && (
              <button
                type='button'
                onClick={submitWelcomeWiz}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Ok, take me to the dashboard!
              </button>
            )}
          </div>
        </div>
      </ModalDailog>
    </>
  );
};

export default LocateShelterDashboard;
